/**
 * Which stored portal accounts an FTC may pick between for a carrier, and how
 * each one is named.
 *
 * Uses the vocabulary of Office → Portal Credentials ("Office account",
 * "Default") so the two surfaces describe the same row the same way. Pure
 * functions, shared by CarrierPortalCredential and its tests.
 */

// Default first, then per-provider accounts by dentist name, then a legacy
// office-wide non-default last. This is the order the API already sends;
// re-applying it here means a caller handing over an unordered list still
// gets the default on top.
export function orderAccounts(accounts) {
  return (accounts || []).slice().sort((a, b) => {
    if (!!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
    const an = (a.provider && a.provider.name) || "";
    const bn = (b.provider && b.provider.name) || "";
    if (an !== bn) {
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn);
    }
    return (a.id || 0) - (b.id || 0);
  });
}

export function defaultAccountId(accounts) {
  const ordered = orderAccounts(accounts);
  return ordered.length ? ordered[0].id : null;
}

// "Dr Emilia Kolanska" / "Office account" — with " · Default" on the one the
// bots use, since that is the account whose 2FA session the scrapes depend on.
export function accountLabel(account) {
  if (!account) return "";
  const raw = account.provider && account.provider.name ? String(account.provider.name).trim() : "";
  const name = raw || "Office account";
  return account.is_default ? `${name} · Default` : name;
}

// The dentist's own claim identifiers, when the office recorded them on the
// Provider. Empty for the office-wide account — its identity is office-level
// and shown on the carrier card instead.
export function providerIdentityRows(account) {
  const p = account && account.provider;
  if (!p) return [];
  const rows = [];
  if (p.license_number) rows.push(["License #", String(p.license_number)]);
  if (p.unique_provider_number) rows.push(["Unique ID", String(p.unique_provider_number)]);
  return rows;
}

// Older backends answer only { exists, credential_id }. Shape that into a
// one-account list so the picker degrades to today's single-login behaviour
// while the frontend and backend ship separately.
export function accountsFromResponse(res) {
  if (!res) return [];
  if (Array.isArray(res.accounts)) return orderAccounts(res.accounts);
  if (res.exists && res.credential_id != null) {
    return [{ id: res.credential_id, is_default: true, provider: null }];
  }
  return [];
}
